import { useApolloClient } from '@apollo/client';
import { isEmpty, isNil } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line max-len
import { type MobilityApplicationEntrypointContextDataFragment } from '../../../../api/fragments/MobilityApplicationEntrypointContextData';
import { useGetApplicationJourneyQuery } from '../../../../api/queries/getApplicationJourney';
import { ApplicationKind } from '../../../../api/types';
import ApplicationSessionModal from '../../../../components/ApplicationSessionExpireModal';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import {
    computeMobilityGTMData,
    type MobilityGTMData,
    MobilityGTMEvents,
} from '../../../../utilities/googleTagManager';
import { usePersistentData } from '../../../../utilities/usePersistData';
import useEndSession from '../../../shared/useEndSession';
import LiveChat from '../../MobilityWebpageEndpoint/Webpage/LiveChat';
import {
    mobilityJourneyTemporaryKey,
    type MobilityJourneyTemporaryValueType,
    useHandleTemporarySession,
} from '../helper';
import CarDetails from './CarDetails';
import { ExtraDataContext } from './ExtraDataContext';

export type CarDetailPageProps = {
    endpoint: MobilityApplicationEntrypointContextDataFragment;
};

type MobilityBookingUpdateState = { token: string; isCancel?: boolean };

const CarDetailPage = ({ endpoint }: CarDetailPageProps) => {
    const state = useLocation().state as MobilityBookingUpdateState | null | undefined;
    const token = state?.token;
    const apolloClient = useApolloClient();
    const [mobilityJourneyTemporaryValue, setMobilityJourneyTemporaryValue] = useState<
        MobilityJourneyTemporaryValueType | undefined
    >();

    const { getItem, setItem, deleteItem } = usePersistentData();
    const { extendTemporarySession } = useHandleTemporarySession();

    useEffect(() => {
        setMobilityJourneyTemporaryValue(getItem(mobilityJourneyTemporaryKey));
    }, [getItem]);

    const handleExtendSession = useCallback(async () => {
        await extendTemporarySession(apolloClient, setItem, mobilityJourneyTemporaryValue);
        setMobilityJourneyTemporaryValue(getItem(mobilityJourneyTemporaryKey));
    }, [apolloClient, extendTemporarySession, getItem, mobilityJourneyTemporaryValue, setItem]);

    const endSession = useEndSession();

    const handleEndSession = useCallback(async () => {
        if (mobilityJourneyTemporaryValue?.token) {
            await endSession(mobilityJourneyTemporaryValue.token, ApplicationKind.Mobility);
            deleteItem(mobilityJourneyTemporaryKey);

            const existedData: MobilityGTMData = {
                event: MobilityGTMEvents.SessionTimedOut,
                data: {
                    stockIdentifier: mobilityJourneyTemporaryValue?.bookingDetails.stockIdentifier,
                    variant: mobilityJourneyTemporaryValue?.bookingDetails.variantName,
                },
            };
            const mobilityGTMData = computeMobilityGTMData(
                MobilityGTMEvents.SessionTimedOut,
                existedData.data.variant,
                existedData.data.stockIdentifier
            );
            window.dataLayer?.push(mobilityGTMData);
        }
    }, [deleteItem, endSession, mobilityJourneyTemporaryValue]);

    const { data, loading } = useGetApplicationJourneyQuery({ variables: { token }, skip: isEmpty(token) });

    const application = data?.result?.application;

    const extra = useMemo(
        () => ({
            application: application?.__typename === 'MobilityApplication' ? application : null,
            token,
            isAmend: !state?.isCancel && !!token && !!application,
            isCancel: state?.isCancel,
        }),
        [application, state?.isCancel, token]
    );

    if (loading) {
        return <PortalLoadingElement />;
    }

    return (
        <>
            {mobilityJourneyTemporaryValue?.token && (
                <ApplicationSessionModal
                    endSession={handleEndSession}
                    expireAt={mobilityJourneyTemporaryValue.expireAt}
                    extendSession={handleExtendSession}
                />
            )}
            <ExtraDataContext.Provider value={extra}>
                <CarDetails endpoint={endpoint} isCancel={state?.isCancel} />
                {!isNil(endpoint.mobilityApplicationModule.liveChatSetting) && (
                    <LiveChat
                        chatSetting={
                            endpoint.mobilityApplicationModule.liveChatSetting.__typename ===
                                'UserlikeChatbotSetting' && endpoint.mobilityApplicationModule.liveChatSetting
                        }
                    />
                )}
            </ExtraDataContext.Provider>
        </>
    );
};

export default CarDetailPage;
